import { useEffect, useState } from "react";
import { Button, Dialog, Field, Icon, IconButton, Select, Tag } from "../../ds/DS";
import { api } from "../../lib/api";
import { fmtSize, type VDocument } from "../data";
import type { PageProps } from "../Portal";
import { DialogFoot, PageHead, ScopeNote, TabBar } from "./parts";

const ACCOUNT = "Client account";
const CLIENT_ACCOUNT = "Your account";
const DOC_KINDS = ["Spec", "Reference", "Notes", "Data", "Contract", "Assets", "Other"];
const KIND_ICON: Record<string, string> = {
  Spec: "file-text",
  Reference: "book-open",
  Notes: "notebook-pen",
  Data: "table",
  Contract: "file-pen-line",
  Assets: "image",
  Other: "file",
};

function UploadDialog({
  clientId,
  projects,
  defaultScope,
  forClient = false,
  onClose,
  onSaved,
}: {
  clientId: string;
  projects: PageProps["projects"];
  defaultScope: string;
  forClient?: boolean; // client "Send us a file": own wording, account = "Your account"
  onClose: () => void;
  onSaved: (toAccount: boolean) => void;
}) {
  const account = forClient ? CLIENT_ACCOUNT : ACCOUNT;
  const [scope, setScope] = useState(defaultScope);
  const [kind, setKind] = useState("Spec");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!file) {
      setErr("Choose a file to upload.");
      return;
    }
    setBusy(true);
    setErr("");
    const projectId =
      scope === account ? "" : (projects.find((p) => p.name === scope)?.id ?? "");
    const form = new FormData();
    form.set("file", file);
    form.set("clientId", clientId);
    form.set("projectId", projectId);
    form.set("kind", kind);
    try {
      await api.upload("/documents", form);
      onSaved(!projectId);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Upload failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      title={forClient ? "Send us a file" : "Upload document"}
      description={
        forClient
          ? "Up to 10 MB. We get a notification as soon as it lands."
          : "Up to 10 MB. Everyone on the account can download it."
      }
      accent
      width={560}
      onClose={onClose}
      footer={
        <DialogFoot
          saveLabel={busy ? "Uploading…" : "Upload"}
          busy={busy}
          invalid={!file}
          onCancel={onClose}
          onSave={() => void submit()}
        />
      }
    >
      <div className="stack-16">
        {err ? <div className="form-err">{err}</div> : null}
        <Field label="File" required>
          <span className="drop">
            <input
              type="file"
              className="drop-input"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <span className="drop-ico">
              <Icon name="upload" size={18} />
            </span>
            <span className="drop-text">
              <span className="drop-name">{file ? file.name : "Choose a file"}</span>
              <span className="drop-sub">
                {file ? fmtSize(file.size) : "PDF, images, spreadsheets or zip"}
              </span>
            </span>
          </span>
        </Field>
        <div className="grid-2">
          <Field label="Belongs to">
            <Select
              options={[account, ...projects.map((p) => p.name)]}
              value={scope}
              onChange={(e) => setScope(e.target.value)}
            />
          </Field>
          <Field label="Type">
            <Select options={DOC_KINDS} value={kind} onChange={(e) => setKind(e.target.value)} />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

export function Documents(props: PageProps) {
  return props.isAdmin ? <AdminDocuments {...props} /> : <ClientFiles {...props} />;
}

/* ---------- client Files (handoff C3 `isCFiles`) ---------- */

function ClientFiles({ client, documents, projects, reload, toast, projectName }: PageProps) {
  const [uploading, setUploading] = useState(false);
  return (
    <div className="stack-24">
      <PageHead
        eyebrow="FILES"
        title="Files"
        sub="Specs, contracts and anything you've sent us."
        actions={
          client ? (
            <Button icon="upload" onClick={() => setUploading(true)}>
              Send us a file
            </Button>
          ) : undefined
        }
      />
      <div className="list-card">
        {documents.length === 0 ? (
          <div className="list-empty">No files yet. Anything we share or you send us lands here.</div>
        ) : (
          documents.map((d) => (
            <div key={d.id} className="cf-row">
              <span className="cf-ico">
                <Icon name={KIND_ICON[d.kind] ?? "file"} size={16} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div className="cf-name">{d.name}</div>
                <div className="cf-sub">
                  {[
                    d.projectId ? projectName(d.projectId) : CLIENT_ACCOUNT,
                    d.kind,
                    `${d.added} · ${d.addedBy}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <span className="cf-size mono">{d.size}</span>
              <a
                className="ds-iconbtn ds-iconbtn--ghost"
                href={`/api/documents/${d.id}/download`}
                aria-label={`Download ${d.name}`}
                title="Download"
              >
                <Icon name="download" size={16} />
              </a>
            </div>
          ))
        )}
      </div>
      {uploading && client ? (
        <UploadDialog
          forClient
          clientId={client.id}
          projects={projects}
          defaultScope={projects[0]?.name ?? CLIENT_ACCOUNT}
          onClose={() => setUploading(false)}
          onSaved={() => {
            reload();
            toast("Sent. We've got it.");
          }}
        />
      ) : null}
    </div>
  );
}

/* ---------- admin Documents ---------- */

function AdminDocuments({
  client,
  documents,
  projects,
  pid,
  setPid,
  reload,
  toast,
  projectName,
}: PageProps) {
  const inProject = pid !== "all";
  const [tab, setTab] = useState<"project" | "client">("project");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (inProject) setTab("project");
  }, [inProject]);

  const del = async (d: VDocument) => {
    await api.del(`/documents/${d.id}`);
    reload();
    toast("Document removed.");
  };

  const projectDocs = documents.filter((d) =>
    inProject ? d.projectId === pid : d.projectId !== null,
  );
  const accountDocs = documents.filter((d) => d.projectId === null);
  const rows = tab === "project" ? projectDocs : accountDocs;
  const showProject = tab === "project" && !inProject;
  const cols = "doc-cols" + (showProject ? " has-project" : "");

  return (
    <div className="stack-24">
      <PageHead
        eyebrow="DOCUMENTS"
        title="Documents"
        sub={`Specs, references and contracts for ${client?.name ?? "this client"}.`}
        actions={
          <Button icon="upload" onClick={() => setUploading(true)}>
            Upload
          </Button>
        }
      />
      <ScopeNote pid={pid} setPid={setPid} projectName={projectName} />

      {inProject ? null : (
        <TabBar
          items={[
            { value: "project", label: "Project documents", count: projectDocs.length },
            { value: "client", label: ACCOUNT, count: accountDocs.length },
          ]}
          value={tab}
          onChange={(v) => setTab(v as "project" | "client")}
        />
      )}

      <div className="list-card">
        <div className={"grid-row grid-head " + cols}>
          <span>File</span>
          {showProject ? <span>Project</span> : null}
          <span>Type</span>
          <span>Size</span>
          <span>Added by</span>
          <span>Added</span>
          <span />
        </div>
        {rows.length === 0 ? (
          <div className="list-empty">No documents here yet.</div>
        ) : (
          rows.map((d) => (
            <div key={d.id} className={"grid-row is-hover " + cols}>
              <div className="doc-file">
                <span className="f-ico">
                  <Icon name={KIND_ICON[d.kind] ?? "file"} size={14} />
                </span>
                <span className="v-label doc-name">{d.name}</span>
              </div>
              {showProject ? (
                <span className="cell-muted" data-label="Project">
                  {d.projectId ? projectName(d.projectId) : "—"}
                </span>
              ) : null}
              <span data-label="Type">{d.kind ? <Tag>{d.kind}</Tag> : "—"}</span>
              <span className="cell-date mono" data-label="Size">
                {d.size}
              </span>
              <span className="cell-muted" data-label="Added by">
                {d.addedBy}
              </span>
              <span className="cell-date mono" data-label="Added">
                {d.added}
              </span>
              <div className="row-6 end">
                <a
                  className="ds-iconbtn ds-iconbtn--ghost"
                  href={`/api/documents/${d.id}/download`}
                  aria-label={`Download ${d.name}`}
                  title="Download"
                >
                  <Icon name="download" size={16} />
                </a>
                <IconButton
                  icon="trash-2"
                  label="Delete"
                  variant="ghost"
                  onClick={() => void del(d)}
                />
              </div>
            </div>
          ))
        )}
      </div>

      {uploading && client ? (
        <UploadDialog
          clientId={client.id}
          projects={projects}
          defaultScope={
            inProject
              ? projectName(pid)
              : tab === "client"
                ? ACCOUNT
                : (projects[0]?.name ?? ACCOUNT)
          }
          onClose={() => setUploading(false)}
          onSaved={(toAccount) => {
            reload();
            if (!inProject) setTab(toAccount ? "client" : "project");
            toast(`Uploaded. ${client.contact ? client.contact.split(/\s+/)[0] : "The client"} can download it now.`);
          }}
        />
      ) : null}
    </div>
  );
}
